// src/modules/talent/dashboard/widgets/shells/HeadcountTrendChart.tsx
import { useQuery } from "@tanstack/react-query";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { Users } from "lucide-react";
import { apiFetch } from "@/modules/shared/lib/api-client";
import { linearForecast } from "@/modules/shared/dashboard/forecast";

type Trend = { points: { month: string; total: number }[] };

export function HeadcountTrendChart() {
  const { data } = useQuery<Trend>({
    queryKey: ["widget", "/talent/dashboard/headcount-trend"],
    queryFn: () => apiFetch<Trend>("/talent/dashboard/headcount-trend"),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  const allPoints = linearForecast(data?.points ?? [], 3);
  const actualPoints = allPoints.filter((p) => !p.projected);
  // Forecast segment: last actual point + all projected points (for connected line)
  const forecastPoints = allPoints.filter((p, i, arr) => {
    if (p.projected) return true;
    // Include the last actual point as the anchor for the dashed line
    const nextIsProjected = arr[i + 1]?.projected;
    return !!nextIsProjected;
  });

  return (
    <div className="ol-card flat col-span-2 flex flex-col gap-3">
      <div className="ol-card-head">
        <div className="ol-card-ic ink"><Users /></div>
        <div className="ol-card-title">
          <div className="ol-card-name"><span className="n">Headcount 6 meses</span></div>
          {allPoints.some((p) => p.projected) && (
            <span className="ol-kpi-l" style={{ fontSize: 11 }}>+ projeção 3 meses</span>
          )}
        </div>
      </div>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={allPoints}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" tick={{ fill: "#6b716b", fontSize: 11 }} />
            <YAxis allowDecimals={false} tick={{ fill: "#6b716b", fontSize: 11 }} />
            <Tooltip />
            {/* Actual line */}
            <Line
              data={actualPoints}
              type="monotone"
              dataKey="total"
              stroke="#0b0d0b"
              strokeWidth={2.5}
              dot={{ r: 4, fill: "var(--lime)", stroke: "#0b0d0b", strokeWidth: 1.5 }}
              isAnimationActive={false}
            />
            {/* Forecast line (dashed) */}
            {forecastPoints.length > 1 && (
              <Line
                data={forecastPoints}
                type="monotone"
                dataKey="total"
                stroke="#0b0d0b"
                strokeWidth={2}
                strokeDasharray="4 4"
                dot={(props: any) => {
                  const { cx, cy, payload } = props;
                  if (!payload?.projected) {
                    return <circle key={`hc-dot-${cx}`} cx={cx} cy={cy} r={4} fill="var(--lime)" stroke="#0b0d0b" strokeWidth={1.5} />;
                  }
                  return <circle key={`hc-dot-${cx}`} cx={cx} cy={cy} r={3} fill="#fff" stroke="#0b0d0b" strokeWidth={1.5} strokeDasharray="2 2" />;
                }}
                isAnimationActive={false}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
